import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}
export function TextInput({
  label,
  className = '',
  ...props
}: TextInputProps) {
  return <div className="mb-4" data-id="element-712">
      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-713">
        {label} {props.required && <span className="text-rose-500" data-id="element-714">*</span>}
      </label>
      <input className={`w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all ${className}`} {...props} data-id="element-715" />
    </div>;
}
interface SelectInputProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: string[];
}
export function SelectInput({
  label,
  options,
  className = '',
  placeholder = 'Select an option',
  ...props
}: SelectInputProps) {
  return <div className="mb-4" data-id="element-716">
      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-717">
        {label} {props.required && <span className="text-rose-500" data-id="element-718">*</span>}
      </label>
      <div className="relative" data-id="element-719">
        <select className={`w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all ${className}`} {...props} data-id="element-720">
          <option value="" disabled data-id="element-721">
            {placeholder}
          </option>
          {options.map(option => <option key={option} value={option} data-id="element-722">
              {option}
            </option>)}
        </select>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-723">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-724">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-725" />
          </svg>
        </div>
      </div>
    </div>;
}
interface MultiSelectChipsProps {
  label: string;
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  accentColor?: 'cyan' | 'purple';
}
export function MultiSelectChips({
  label,
  options,
  selectedValues,
  onChange,
  accentColor = 'cyan'
}: MultiSelectChipsProps) {
  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };
  const activeBg = accentColor === 'cyan' ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300' : 'bg-purple-500/20 border-purple-500 text-purple-300';
  const inactiveBg = 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500';
  return <div className="mb-6" data-id="element-726">
      <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-727">
        {label}
      </label>
      <div className="flex flex-wrap gap-2" data-id="element-728">
        {options.map(option => {
        const isSelected = selectedValues.includes(option);
        return <motion.button key={option} type="button" onClick={() => toggleValue(option)} whileTap={{
          scale: 0.95
        }} animate={{
          scale: isSelected ? 1.05 : 1
        }} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all flex items-center gap-2 ${isSelected ? activeBg : inactiveBg}`} data-id="element-729">
              {isSelected && <Check size={14} data-id="element-730" />}
              {option}
            </motion.button>;
      })}
      </div>
    </div>;
}
interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
}
export function TextArea({
  label,
  className = '',
  ...props
}: TextAreaProps) {
  return <div className="mb-4" data-id="element-731">
      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-732">
        {label} {props.required && <span className="text-rose-500" data-id="element-733">*</span>}
      </label>
      <textarea className={`w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all resize-none ${className}`} rows={4} {...props} data-id="element-734" />
    </div>;
}
interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  accentColor?: 'cyan' | 'purple';
}
export function ProgressBar({
  currentStep,
  totalSteps,
  accentColor = 'cyan'
}: ProgressBarProps) {
  const progress = currentStep / totalSteps * 100;
  const barColor = accentColor === 'cyan' ? 'bg-cyan-500' : 'bg-purple-500';
  return <div className="w-full bg-slate-800 h-2 rounded-full mb-8 overflow-hidden" data-id="element-735">
      <motion.div className={`h-full ${barColor}`} initial={{
      width: 0
    }} animate={{
      width: `${progress}%`
    }} transition={{
      duration: 0.5,
      ease: 'easeInOut'
    }} data-id="element-736" />
    </div>;
}